import {
  LeftOutlineIcon,
  PlayIcon,
  RightOutlineIcon,
} from '@carboncopyco/icons';
import {
  Badge,
  Button,
  ColorHex,
  TextBase,
  TextCaption,
  TextLargeParagraph,
  TextLargeParagraphTitle,
  TooltipBubble,
} from '@carboncopyco/ui';
import React, {
  type MouseEventHandler,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Screenshot } from '../Screenshot';
import type { SpeakerViewItem } from './SpeakerViewScreen';

const SCROLL_DISTANCE = 165;
interface WindowsScreenProps {
  items: SpeakerViewItem[];
  activePresentation: number;
  setActivePresentation: (index: number) => void;
}
interface windowItemProps {
  items: SpeakerViewItem;
  onClick: MouseEventHandler;
  disabled: boolean;
}
export const Item = ({
  items: { presentationName, image, screenshotImageType },
  disabled,
  onClick,
}: windowItemProps) => {
  return (
    <div
      className={`select-none shrink-0 rounded-md overflow-hidden h-ds-26 relative border-2 ${
        !disabled
          ? 'border-ds-primary-300 hover:shadow-lg'
          : 'border-transparent opacity-50 hover:opacity-75'
      } `}
      onClick={onClick}
      style={{ width: `${SCROLL_DISTANCE}px` }}
    >
      <Button className="flex h-full w-full">
        <Screenshot
          noBorder
          image={image}
          alt="screenshot"
          isOverlayDemo={screenshotImageType === 'overlay'}
          isOverlayTour={screenshotImageType === 'tour'}
          isUrl={screenshotImageType === 'url'}
        />
      </Button>
      <div className="h-8 w-full absolute left-0 bottom-0 bg-ds-natural-600/60 flex items-center select-none">
        <div
          className={`flex px-2 ${disabled ? 'max-w-37.5' : 'max-w-[136px] w-[136px]'}`}
        >
          <TextBase
            label={presentationName}
            color="text-ds-accent3-100"
            ellipsis
          />
        </div>
        {!disabled && (
          <PlayIcon
            color={ColorHex.accent3}
            size={16}
          />
        )}
      </div>
    </div>
  );
};
const useDetectScroll = (node) => {
  const [isStart, setIsStart] = useState(true);
  const [isLeft, setIsLeft] = useState(false);
  const handleScroll = useCallback(() => {
    const { scrollLeft, scrollWidth, clientWidth } = node.current;
    setIsLeft(scrollWidth - clientWidth === Math.round(scrollLeft));
    setIsStart(scrollLeft === 0);
  }, [node]);
  const changeScroll = (dist: number, override = false) => {
    if (node.current && !Number.isNaN(node?.current?.scrollLeft)) {
      if (override) {
        if (dist !== node.current.scrollLeft) {
          node.current.scrollLeft = dist;
        }
      } else {
        node.current.scrollLeft += dist;
      }
    }
  };
  useEffect(() => {
    const element = node.current;
    element?.addEventListener('scroll', handleScroll);
    return () => {
      element?.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, node]);
  return { isLeft, isStart, changeScroll };
};
export const WindowsScreen = ({
  items,
  activePresentation,
  setActivePresentation,
}: WindowsScreenProps) => {
  const listRef = React.useRef<HTMLDivElement>(null);
  const { isLeft, isStart, changeScroll } = useDetectScroll(listRef);
  const shouldDisplayArrow = items.length > 2;
  const changeSelected = (index) => {
    setActivePresentation(index);
    changeScroll(index * SCROLL_DISTANCE, true);
  };
  useEffect(() => {
    changeSelected(activePresentation);
  }, []);

  return (
    <div className="bg-inherit w-full h-ds-260  rounded-md mt-px p-5 flex flex-col select-none">
      <TextLargeParagraphTitle label="Toggle between demos" />
      <div className="flex flex-row  pt-2 gap-1">
        <TextCaption
          label="Next:"
          color="text-ds-natural-300"
        />
        <Badge
          label="⌃⇧N"
          variant="disabled"
        />
        <TextCaption
          label="Back:"
          color="text-ds-natural-300"
        />
        <Badge
          label="⌃⇧B"
          variant="disabled"
        />
      </div>
      <div
        className="flex flex-1 w-full overflow-x-auto relative scroll-smooth scrollbar-hide gap-2 py-5"
        ref={listRef}
      >
        {items.map(
          ({ presentationName, image, id, screenshotImageType }, index) => (
            <TooltipBubble
              key={id}
              title={presentationName}
              placement="bottom-start"
              offset={[10, -65]}
              absolute
            >
              <Item
                onClick={() => {
                  changeSelected(index);
                }}
                items={{
                  presentationName,
                  image,
                  id,
                  screenshotImageType,
                }}
                disabled={index !== activePresentation}
              />
            </TooltipBubble>
          )
        )}
      </div>
      <div className="flex items-center justify-center select-none">
        {shouldDisplayArrow && (
          <Button
            disabled={isStart}
            faded
            onClick={() => changeScroll(-SCROLL_DISTANCE)}
            icon={
              <LeftOutlineIcon
                color={ColorHex.accent3}
                size={20}
              />
            }
          />
        )}
        <TooltipBubble
          title={items[activePresentation].presentationName}
          placement="top"
        >
          <div
            className="py-1 max-w-[325px]"
            onClick={() => changeSelected(activePresentation)}
          >
            <TextLargeParagraph
              ellipsis
              label={items[activePresentation].presentationName}
              color="text-ds-accent3-100"
            />
          </div>
        </TooltipBubble>
        {shouldDisplayArrow && (
          <Button
            disabled={isLeft}
            faded
            onClick={() => changeScroll(SCROLL_DISTANCE)}
            icon={
              <RightOutlineIcon
                color={ColorHex.accent3}
                size={20}
              />
            }
          />
        )}
      </div>
    </div>
  );
};
